import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import ReactImageUploading from "react-images-uploading";
import {ImageListType} from "react-images-uploading/dist/typings";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {SetImagePackSlotCommand} from "../../commands/setImagePackSlot.command";
import {SetMetadataIconCommand} from "../../commands/setMetadataIcon.command";
import {SetBuildingConstructionImageCommand} from "../../commands/setBuildingConstructionImage.command";
import {useImageUpload, freshSlotKey, freshConstructionKey, UploadTarget} from "../../utils/useImageUpload";
import {ChangeConstantsCommand} from "../../commands/changeConstants.command";
import {classifyImageKeys} from "../../utils/imageClassification";
import {offerOrphanImageCleanup} from "../../utils/orphanImageCleanup";
import {computePixelMultiplier} from "../../utils/exportTransform";
import {apiBindings} from "../../utils/constants";
import {ArchiveData} from "../../models/remoteConstants.model";

// Read a (cross-origin) archived image's natural dimensions; undefined if it
// can't load. Archive entries are bare {fileName, key} — no stored dimensions —
// so we recover them from the file, exactly like the Archive tab's Restore.
function loadDimensions(url: string): Promise<{w: number; h: number} | undefined> {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve({w: img.naturalWidth, h: img.naturalHeight});
        img.onerror = () => resolve(undefined);
        img.src = url;
    });
}

// The picker's target type is the shared upload target (re-exported so existing
// imports of ImagePickerTarget keep working). freshSlotKey and the upload path
// itself now live in useImageUpload, shared with the asset-pack popup.
export type ImagePickerTarget = UploadTarget;

interface ImagePickerProps {
    target: ImagePickerTarget;
    currentKey?: string;     // currently-assigned key, used to highlight selection
    onClose(): void;
}

// Modal for assigning an image. Offers two paths: upload a brand-new file,
// or pick an already-uploaded image key from state.images. The pick path is
// a pure data write — no file traffic — which makes re-skinning across
// entities trivial and keeps image sharing visible as real references.
function ImagePicker(props: ImagePickerProps) {
    const {state, executeCommand, goBack} = useIntegrator();
    const {getImageUrl, getLocalizationValue, confirm, choose} = useApp();
    const {applyUpload} = useImageUpload();
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const selectedThumbRef = useRef<HTMLDivElement | null>(null);

    // On open, scroll the currently-selected thumbnail into the middle of the
    // visible grid so the user lands looking at "the one they already have".
    // Runs once at mount — subsequent scrolling is the user's to manage.
    useEffect(() => {
        selectedThumbRef.current?.scrollIntoView({block: 'center'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Names/orphan state are derived — same classification the Images tab
    // uses, so an image renders with the same name in both places.
    const classification = useMemo(
        () => classifyImageKeys(state, getLocalizationValue),
        [state, getLocalizationValue]
    );

    const allKeys = useMemo(() => {
        return Object.keys(state.images ?? {});
    }, [state.images]);

    const labeledKeys = useMemo(() => {
        return allKeys
            .map(key => {
                const info = classification.get(key);
                return {key, label: info?.label ?? key, orphan: (info?.usedBy.length ?? 0) === 0};
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [allKeys, classification]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return labeledKeys;
        return labeledKeys.filter(({key, label}) =>
            label.toLowerCase().includes(q) || key.toLowerCase().includes(q));
    }, [labeledKeys, search]);

    // Archived images (PNG files only — this picker assigns images). Selecting
    // one un-archives the file onto the target; see pickArchived.
    const archivePngs = useMemo(() =>
        (state.archive ?? []).filter(a => a.fileName.split('.').pop()?.toLowerCase() === 'png'),
        [state.archive]);

    const archiveFiltered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return archivePngs;
        return archivePngs.filter(a =>
            a.key.toLowerCase().includes(q) || a.fileName.toLowerCase().includes(q));
    }, [archivePngs, search]);

    async function pickExisting(key: string) {
        // Bare image key (orders): clone the picked image's record onto the key.
        // The client reads images[key] directly, so there's no slot/indirection
        // to wire and nothing to orphan (we overwrite the key in place).
        if (props.target.kind === 'imageKey') {
            const targetKey = props.target.key;
            executeCommand(new ChangeConstantsCommand(data => {
                const src = data.images?.[key];
                if (src && data.images) data.images[targetKey] = {...src};
            }));
            props.onClose();
            return;
        }

        if (props.target.kind === 'packSlot') {
            const t = props.target;
            executeCommand(new SetImagePackSlotCommand(t.packId, t.level, t.variation, key));
        } else if (props.target.kind === 'buildingConstruction') {
            const t = props.target;
            executeCommand(new SetBuildingConstructionImageCommand(t.buildingId, t.index, key));
        } else {
            executeCommand(new SetMetadataIconCommand(props.target.uid, key));
        }
        // Re-pointing may have orphaned the previously-assigned image. If the
        // displaced key was the entity's bare id (the retired "entity image"),
        // archive it silently; any other orphan gets the archive/delete/undo
        // offer before the picker closes.
        if (props.currentKey && props.currentKey !== key) {
            const isRetiredEntityImage =
                props.target.kind === 'packSlot' && props.currentKey === props.target.packId;
            await offerOrphanImageCleanup(props.currentKey,
                {state, getLocalizationValue, choose, executeCommand, goBack},
                isRetiredEntityImage ? 'auto-archive' : 'undo');
        }
        props.onClose();
    }

    // Select an archived image: un-archive its file onto the target. Same
    // keying as the upload path (occupied slot replaces in place; empty slot
    // uses its positional key; metadata uses the uid) — but the file already
    // exists in the blob store, so we just (re)create the images[] entry that
    // points at it, recompute its dimensions/PPU, wire the target, and drop the
    // file from the archive (it's live again). Note: the archive stores no
    // pivot/scale, so a fresh entry comes in at default tuning.
    async function pickArchived(entry: ArchiveData) {
        const targetKey = props.target.kind === 'packSlot'
            ? (props.currentKey ?? freshSlotKey(state.images ?? {}, props.target.packId, props.target.level, props.target.variation))
            : props.target.kind === 'buildingConstruction'
                ? (props.currentKey ?? freshConstructionKey(state.images ?? {}, props.target.buildingId))
                : props.target.kind === 'metadataIcon'
                    ? props.target.uid
                    : props.target.key;   // imageKey — written directly, no wiring

        // Replacing an image several things share changes all of them — same
        // confirm guard the upload path uses.
        const usages = classification.get(targetKey)?.usedBy ?? [];
        if (targetKey in (state.images ?? {}) && usages.length > 1) {
            const proceed = await confirm({
                title: 'Replace shared image?',
                body: (
                    <div className={'layout vertical gap'}>
                        <p>The image at this key is used in <strong>{usages.length}</strong> places —
                            choosing this changes all of them:</p>
                        {usages.map((u, i) => <p key={i} className={'muted-text'}>• {u.label}</p>)}
                    </div>
                ),
                confirmText: 'Replace everywhere',
                danger: true,
            });
            if (!proceed) return;
        }

        setLoading(true);
        const dims = await loadDimensions(`${apiBindings.images}${entry.fileName}`);
        setLoading(false);

        executeCommand(new ChangeConstantsCommand(data => {
            // 1) (Re)create the images[] entry for the archived file.
            if (targetKey in data.images) {
                const cur = data.images[targetKey];
                const displaced = cur.fileName;
                cur.fileName = entry.fileName;
                if (dims) {
                    cur.srcWidth = dims.w;
                    cur.srcHeight = dims.h;
                    cur.pixelMultiplier = computePixelMultiplier(dims.w, cur.scale);
                }
                // The file we replaced in place is now loose — archive it.
                if (displaced && displaced !== entry.fileName) {
                    data.archive.push({fileName: displaced, key: targetKey});
                }
            } else {
                data.images[targetKey] = {
                    fileName: entry.fileName,
                    pixelMultiplier: dims ? computePixelMultiplier(dims.w, undefined) : 800,
                    offsetX: 0.5,
                    offsetY: 0.5,
                    srcWidth: dims?.w,
                    srcHeight: dims?.h,
                };
            }

            // 2) Wire the target.
            if (props.target.kind === 'packSlot') {
                const t = props.target;
                const pack = data.imagePacks[t.packId];
                if (pack) {
                    while (pack.length <= t.level) pack.push([]);
                    const row = pack[t.level];
                    if (t.variation < row.length) row[t.variation] = targetKey;
                    else row.push(targetKey);
                }
            } else if (props.target.kind === 'buildingConstruction') {
                const t = props.target;
                if (!data.constructionImagePacks) data.constructionImagePacks = {};
                const frames = data.constructionImagePacks[t.buildingId] ?? [];
                if (t.index <= frames.length) {
                    frames[t.index] = targetKey;
                    data.constructionImagePacks[t.buildingId] = frames;
                }
            } else if (props.target.kind === 'metadataIcon' && data.imagePacks && props.target.uid in data.imagePacks) {
                // Metadata icon: clear any pick-indirection so the uid-keyed
                // entry we just wrote actually displays.
                delete data.imagePacks[props.target.uid];
            }

            // 3) The archived file is live again — remove it from the archive.
            const ai = data.archive.findIndex(a => a.fileName === entry.fileName);
            if (ai >= 0) data.archive.splice(ai, 1);
        }));

        // Clearing a metadata indirection may have orphaned a previously
        // borrowed key — offer archive/delete/keep (keep-mode: the pick isn't a
        // single clean undo step for the helper to revert).
        if (props.target.kind === 'metadataIcon' && props.currentKey && props.currentKey !== targetKey) {
            await offerOrphanImageCleanup(props.currentKey,
                {state, getLocalizationValue, choose, executeCommand, goBack}, 'keep');
        }
        props.onClose();
    }

    async function handleUploadChange(value: ImageListType) {
        const file = value[0]?.file;
        if (!file) return;
        // The whole upload/replace/wire/archive flow lives in useImageUpload,
        // shared with the asset-pack popup. Close only on success (a cancelled
        // shared-image confirm or a failed upload leaves the picker open).
        const ok = await applyUpload(props.target, props.currentKey, file, setLoading);
        if (ok) props.onClose();
    }

    return createPortal(
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical gap box'}
                 style={{padding: 15, gap: 10, width: 1040, maxHeight: '85vh', overflow: 'hidden'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Choose Image</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>

                <div className={'layout horizontal gap'} style={{gap: 10, alignItems: 'center'}}>
                    <ReactImageUploading value={[]} onChange={handleUploadChange} maxNumber={1}>
                        {({onImageUpload}) => (
                            <button onClick={onImageUpload} disabled={loading}>
                                <span className={'fa-solid fa-upload'} style={{marginRight: 6}}/>
                                Upload new
                            </button>
                        )}
                    </ReactImageUploading>
                    <input style={{flexGrow: 1, padding: 5}}
                           placeholder={'Search...'}
                           value={search}
                           autoFocus
                           onChange={e => setSearch(e.target.value)}/>
                    <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12}}>
                        {filtered.length} of {allKeys.length}
                    </p>
                </div>

                <div className={'layout vertical'}
                     style={{gap: 10, overflowY: 'auto', paddingRight: 6, minHeight: 0}}>
                    <div className={'layout horizontal gap'} style={{flexWrap: 'wrap', gap: 4}}>
                    {filtered.map(({key, label, orphan}) => {
                        const selected = key === props.currentKey;
                        const usages = classification.get(key)?.usedBy ?? [];
                        const tooltipParts: string[] = [];
                        tooltipParts.push(orphan ? `${label} (unused)` : label);
                        tooltipParts.push(key);
                        usages.forEach(u => tooltipParts.push(`• ${u.label}`));
                        const tooltip = tooltipParts.join('\n');
                        return (
                            <div key={key} className={'layout vertical center'} style={{width: 120}}
                                 ref={selected ? selectedThumbRef : undefined}>
                                <div className={`image-cell${selected ? ' selected' : ''}`}
                                     style={{
                                         cursor: 'pointer',
                                         width: 115, height: 115,
                                         opacity: orphan ? 0.6 : 1,
                                     }}
                                     onClick={() => pickExisting(key)}
                                     title={tooltip}>
                                    <img alt={label} src={getImageUrl(key)}
                                         style={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                                </div>
                                <p style={{
                                    fontSize: 'var(--font-size-default)',
                                    boxSizing: 'border-box', maxWidth: 116, width: 116,
                                    padding: '1px 2px',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    // Reserve two lines so the grid stays even whether a
                                    // label is one line or wraps to two.
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.2,
                                    minHeight: '2.4em',
                                    fontStyle: orphan ? 'italic' : 'normal',
                                    color: selected ? 'var(--tint-color)' : 'rgba(255,255,255,0.66)',
                                }}
                                   title={tooltip}>
                                    {label}{orphan ? ' •' : ''}
                                </p>
                            </div>
                        );
                    })}
                    {filtered.length === 0 &&
                        <p style={{color: 'rgba(255,255,255,0.66)'}}>No matching live images.</p>}
                    </div>

                    {archiveFiltered.length > 0 && <>
                        <p className={'muted-text'} style={{
                            borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 8, fontSize: 12,
                        }}>
                            <span className={'fa-solid fa-box-archive'} style={{marginRight: 6}}/>
                            Archive ({archiveFiltered.length}) — select to use here
                        </p>
                        <div className={'layout horizontal gap'} style={{flexWrap: 'wrap', gap: 4}}>
                            {archiveFiltered.map((entry, i) => {
                                const tip = `${getLocalizationValue(entry.key)}\n${entry.key}\n${entry.fileName}\n(archived — select to use here)`;
                                return (
                                    <div key={`arch-${entry.fileName}-${i}`} className={'layout vertical center'} style={{width: 120}}>
                                        <div className={'image-cell'}
                                             style={{cursor: 'pointer', width: 115, height: 115, opacity: 0.85, position: 'relative'}}
                                             onClick={() => pickArchived(entry)}
                                             title={tip}>
                                            <img alt={entry.key} src={`${apiBindings.images}${entry.fileName}`}
                                                 style={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                                            <span className={'fa-solid fa-box-archive'}
                                                  style={{position: 'absolute', top: 4, right: 4, fontSize: 11, opacity: 0.85}}/>
                                        </div>
                                        <p style={{
                                            fontSize: 'var(--font-size-default)',
                                            boxSizing: 'border-box', maxWidth: 116, width: 116,
                                            padding: '1px 2px', textAlign: 'center',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            fontStyle: 'italic', color: 'rgba(255,255,255,0.66)',
                                        }} title={tip}>
                                            {getLocalizationValue(entry.key)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </>}
                </div>

                {loading && <p style={{color: 'rgba(255,255,255,0.66)'}}>Loading...</p>}
            </div>
        </div>,
        document.body,
    );
}

export default ImagePicker;
